---
title: "How to run a cold email campaign"
description: "Set up a sending domain, build a sequence, launch it to a tagged audience and handle replies in the Inbox, all from inside NerdCRM."
publishedAt: "2026-08-14"
updatedAt: "2026-10-25"
slug: "how-to-run-a-cold-email-campaign"
---

# How to run a cold email campaign

An email campaign sends a sequence of cold emails to your contacts and brings every reply back to the **Inbox**, threaded onto the contact record, so a warm lead lands in your CRM rather than someone's personal inbox.

Cold email is a paid feature. The free plan includes **300 outreach emails** — enough for a realistic first campaign before deciding — see [your free allowance](/help/free-allowance-and-upgrading).

Email campaigns and the Email side of the Inbox are for admins and owners. The sending-domain page in step 1 is owner only.

## Step 1 — Connect a sending domain

Open **Campaigns** and click **Sending settings** (choose **Email** if it shows a menu). You can also open it from the **Email outreach** entry under **Channels** in Settings. You'll need a Mailgun account and a sending domain (for example `mail.yourcompany.com`). The page is titled **Email outreach**, and its **Setup guide** walks through everything below, with the exact DNS values to copy (open it from the **Setup guide** link under **Replies & tracking**).

1. In Mailgun, add the domain and complete its DNS records: SPF, DKIM and DMARC, plus the tracking CNAME.
2. In the **Sending domain** card, enter the **Mailgun sending domain**. Under **Who it comes from**, paste its **Mailgun API key**.
3. Turn **Outreach on** (the switch at the top of the page), and under **Who it comes from** set the **From name** and **From email** your recipients will see. The From email must be an address on your verified sending domain. Click **Save changes** in the bar that appears.
4. To receive replies, point the domain's MX records at Mailgun and add the inbound route and event webhook shown in the setup guide (steps 6 and 7).

Use a subdomain, never your main company domain. If a cold campaign damages a domain's reputation, you want that contained to the subdomain and not affecting the email your team relies on.

## Step 2 — Warm the domain up

A brand-new domain that sends 500 emails on day one will land in spam. Under **Sending limits**, turn on **Warm-up ramp** and NerdCRM will ramp the daily limit for you, starting at the number in **Start at (per day)** and adding the **Add each day** number every day.

While you're warming up, also set:

- **Send window** — only send on the days and hours you choose. Cold email that arrives at 3am reads as automated.
- **One email every** *N* minutes — a minimum gap between messages, so you trickle rather than burst.
- **Daily cap** — a hard ceiling per day regardless of everything else.
- **Don't re-contact for** *N* days — how long to wait before emailing the same person again.

Not ready to send for real? Turn on **Test mode**: the whole pipeline runs, but every send is logged instead of emailed. Turn it off again before your first real campaign.

## Step 3 — Tag your audience

An email campaign sends to everyone who carries its **Target tag**. Before you build the campaign, tag the contacts you want to reach: select them on **Contacts**, click **Add tag**, or include a **Tags** column when you [import them from a CSV](/help/how-to-import-contacts-from-csv).

## Step 4 — Build the sequence

On **Campaigns**, click **New campaign**. If your organization also uses WhatsApp, choose **Email** from the menu. Name the campaign, enter the **Target tag**, check the **From name** and **From email**, and click **Create**.

The **Sequence** on the left is a list of steps, each with a delay. Select a step to edit it:

- **Step 1** — **Delay (days)** is 0: the initial email.
- **Step 2** — for example day 3, a short follow-up. Follow-ups thread onto the original message, so they appear in the same conversation.
- **Step 3** — for example day 7, a final short note.

Click **+ Add step** for more. Give each step a **Subject** and a **Body**. Use **+ First name** and **+ Last name** to personalise (they insert `{{firstName}}` and `{{lastName}}`). Keep it short — three or four sentences beats a wall of text.

Click **Preview** to see how each step looks, and **Send test** to email step 1 to yourself. Read it on a phone before it goes anywhere near a prospect. **Save** as you go.

The cards on the right show the **Audience** (how many contacts carry the tag) and the **Sending** pace from your settings.

## Step 5 — Review & launch

When the draft is ready, click **Review & launch**. The summary shows:

- **Audience** — the tag
- **Recipients** — exactly how many people will be enrolled
- **From** and **Sequence** — the sender and the number of emails
- **Free allowance** — a warning if the campaign is larger than your remaining free allowance

Click **Send now** to start. Anyone who has unsubscribed, bounced, or was contacted within your cooldown window is excluded automatically. Use **Pause** and **Resume** on the campaign to stop and restart sending; the **Recipients** list at the bottom shows each person's status.

## Step 6 — Handle replies

Replies land in the **Inbox**, matched to the contact who sent them. Open the **Email** tab, or turn on **Needs reply**, to see who is waiting. Replying from there:

- keeps the whole thread on the contact record,
- **doesn't consume your free allowance** — once someone writes to you, the conversation is free.

When a contact replies, their sequence stops, so nobody gets a follow-up after they've already answered. Use **Done** to mark a thread handled, and **Reassign owner** to hand the contact to a teammate. The Email tab's **Status** filter narrows the list to Replied, Sent, Opened, Bounced, Unsubscribed or Failed.

## Troubleshooting

| Symptom | Likely cause |
|---|---|
| Campaign paused on its own | Free allowance used up, or the daily cap hit. Check the campaign's status |
| Emails not sending at all | Outside the send window, **Outreach on** is off, or the sending domain isn't verified in Mailgun |
| Replies not appearing in the Inbox | The inbound route isn't configured. Follow step 6 of the **Setup guide** on the Email outreach page |
| A reply isn't linked to a contact | Admins find these under **Unmatched** in the Inbox. You can read them, but not reply from there |
| High bounce rate | Your list needs cleaning. Validate addresses before tagging them |
